import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import type { IToastService, Toast, ToastOptions, ToastPosition } from 'continity-shared-ui';

/**
 * {@link IToastService} implementation for the standalone Phoenix portal.
 *
 * shared-ui ships the toast VISUALS (`app-toast`, `app-toast-container`) and the `TOAST_SERVICE`
 * seam, but no implementation — every app binds its own, exactly like `SETTINGS_SERVICE`. The
 * main portal binds its `ToastService`; this app had no binding at all, so `app-toast-container`
 * could not be rendered here and any shared-ui component reaching for `TOAST_SERVICE` would die
 * with NG0201 (the same failure mode that once left the /runs table empty).
 *
 * Behaviour mirrors the main portal's service where it is observable to the user — the default
 * durations, error toasts sticky until dismissed, and a cap on how many stack up. It is
 * deliberately NOT a copy of that queue: the main portal re-displays hidden toasts as slots free
 * up, which needs its full bookkeeping; here the oldest is simply dropped. If the two ever need
 * to behave identically, the honest fix is one implementation in `continity-shared-shell`, not a
 * second copy of the queue.
 */
@Injectable({ providedIn: 'root' })
export class PhoenixToastService implements IToastService {
  private readonly toastSubject = new Subject<Toast>();
  private readonly positionSubject = new BehaviorSubject<ToastPosition>('top-right');
  private readonly removeToastSubject = new Subject<string>();

  readonly toast$: Observable<Toast> = this.toastSubject.asObservable();
  readonly position$: Observable<ToastPosition> = this.positionSubject.asObservable();
  readonly removeToast$: Observable<string> = this.removeToastSubject.asObservable();

  /** Ids in display order, oldest first — the eviction queue for {@link MAX_TOASTS}. */
  private readonly visible: string[] = [];
  private readonly MAX_TOASTS = 5;
  private sequence = 0;

  /** Error toasts stay until dismissed; the rest auto-close. Same values as the main portal. */
  private readonly defaultDurations: Record<Toast['type'], number> = {
    success: 3000,
    error: 0,
    warning: 4000,
    info: 3000,
  };

  showSuccess(title: string, message?: string, duration?: number): void {
    this.show({ type: 'success', title, message, duration });
  }

  showError(title: string, message?: string, duration?: number): void {
    this.show({ type: 'error', title, message, duration });
  }

  showWarning(title: string, message?: string, duration?: number): void {
    this.show({ type: 'warning', title, message, duration });
  }

  showInfo(title: string, message?: string, duration?: number): void {
    this.show({ type: 'info', title, message, duration });
  }

  show(options: ToastOptions): void {
    const toast: Toast = {
      ...options,
      id: `phoenix-toast-${++this.sequence}`,
      duration: options.duration ?? this.defaultDurations[options.type],
      closable: options.closable ?? true,
    };

    // Drop the oldest rather than letting the stack grow without bound.
    while (this.visible.length >= this.MAX_TOASTS) {
      const oldest = this.visible.shift();
      if (oldest) this.removeToastSubject.next(oldest);
    }

    this.visible.push(toast.id);
    this.toastSubject.next(toast);
  }

  removeToast(id: string): void {
    const index = this.visible.indexOf(id);
    if (index >= 0) this.visible.splice(index, 1);
    this.removeToastSubject.next(id);
  }

  setPosition(position: ToastPosition): void {
    this.positionSubject.next(position);
  }
}
